import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, RefreshCw, X, Download } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FilterSidebar = ({ filters, setFilters, onRefresh }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [availableQueries, setAvailableQueries] = useState([]);
  const [availableDatasets, setAvailableDatasets] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [tagsRes, queriesRes, datasetsRes] = await Promise.all([
        axios.get(`${API}/analytics/tags`, { withCredentials: true }),
        axios.get(`${API}/analytics/queries`, { withCredentials: true }),
        axios.get(`${API}/analytics/datasets`, { withCredentials: true })
      ]);

      setAvailableTags(tagsRes.data.distribution.map(t => t.tag));
      setAvailableQueries(queriesRes.data.distribution.map(q => q.query));
      setAvailableDatasets(datasetsRes.data.datasets || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/analytics/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const unannotatedCount = stats ? stats.total_tweets - stats.total_annotations : 0;

  const handleClearFilters = () => {
    setFilters({ search: '', tags: [], bookmarked: false, query: '', dataset: '' });
  };

  const handleTagToggle = (tag) => {
    setFilters(prev => {
      const newTags = prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      return { ...prev, tags: newTags };
    });
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `annotations_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const hasActiveFilters = filters.search || filters.tags.length > 0 || filters.bookmarked || filters.query || filters.dataset;

  return (
    <div className="w-80 bg-white border-r border-gray-200 h-screen overflow-y-auto sticky top-0" data-testid="filter-sidebar">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              data-testid="clear-filters-button"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Stats Summary */}
        {stats && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Tweets:</span>
                <Badge variant="secondary">{stats.total_tweets}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Annotated:</span>
                <Badge variant="secondary">{stats.total_annotations}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bookmarked:</span>
                <Badge variant="secondary">{stats.total_bookmarked}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Unique Tags:</span>
                <Badge variant="secondary">{stats.unique_tags}</Badge>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Unannotated:</span>
                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                    {unannotatedCount}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Search Content</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search keywords..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Tag Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Filter by Tags
            {filters.tags.length > 0 && (
              <span className="ml-2 text-xs text-blue-600">({filters.tags.length} selected)</span>
            )}
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-gray-50">
            {availableTags.length > 0 ? (
              availableTags.map(tag => (
                <div key={tag} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tag-${tag}`}
                    checked={filters.tags.includes(tag)}
                    onCheckedChange={() => handleTagToggle(tag)}
                    data-testid={`tag-checkbox-${tag}`}
                  />
                  <label
                    htmlFor={`tag-${tag}`}
                    className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                  >
                    {tag}
                  </label>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-2">No tags yet</p>
            )}
          </div>
        </div>

        {/* Dataset Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Filter by Dataset/File</label>
          <Select
            value={filters.dataset}
            onValueChange={(value) => setFilters({ ...filters, dataset: value === 'all' ? '' : value })}
          >
            <SelectTrigger data-testid="dataset-filter-select">
              <SelectValue placeholder="All datasets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All datasets</SelectItem>
              {availableDatasets.map(dataset => (
                <SelectItem key={dataset} value={dataset}>{dataset}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {availableDatasets.length === 0 && (
            <p className="text-xs text-gray-500">Upload multiple files to filter by dataset</p>
          )}
        </div>

        {/* Query Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Filter by Query</label>
          <Select
            value={filters.query}
            onValueChange={(value) => setFilters({ ...filters, query: value === 'all' ? '' : value })}
          >
            <SelectTrigger data-testid="query-filter-select">
              <SelectValue placeholder="All queries" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All queries</SelectItem>
              {availableQueries.map(query => (
                <SelectItem key={query} value={query}>{query}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Bookmarked Filter */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="bookmarked"
            checked={filters.bookmarked}
            onCheckedChange={(checked) => setFilters({ ...filters, bookmarked: checked })}
            data-testid="bookmarked-checkbox"
          />
          <label
            htmlFor="bookmarked"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show bookmarked only
          </label>
        </div>

        {/* Refresh Button */}
        <Button
          onClick={onRefresh}
          variant="outline"
          className="w-full"
          data-testid="refresh-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>

        {/* Export Section */}
        <div className="space-y-2 pt-4 border-t">
          <label className="text-sm font-medium text-gray-700">Export Annotations</label>
          <div className="space-y-2">
            <Button
              onClick={() => handleExport('json')}
              variant="outline"
              className="w-full"
              data-testid="export-json-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export as JSON
            </Button>
            <Button
              onClick={() => handleExport('csv')}
              variant="outline"
              className="w-full"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export as CSV
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;